import { APP_CONSTANTS } from '@/config/constants';

type ImageTracePayload = Record<string, any>;

const MAX_IMAGE_TRACES = 1_000;
const traceBuffer: { ts: number; event: string; payload: ImageTracePayload }[] = [];

export function traceImage(event: string, payload: ImageTracePayload = {}): void {
  try {
    if (!__DEV__ || !APP_CONSTANTS?.DEBUG?.IMAGE_TRACE) return;
    const record = { ts: Date.now(), event, payload };
    // Log estruturado no console – fácil de filtrar por [IMG_TRACE]
    // e fácil de remover depois (arquivo único e flag global)
    // eslint-disable-next-line no-console
    console.log('[IMG_TRACE]', record);
    traceBuffer.push(record);
    if (traceBuffer.length > MAX_IMAGE_TRACES) {
      traceBuffer.splice(0, traceBuffer.length - MAX_IMAGE_TRACES);
    }
  } catch {
    // Evita qualquer quebra por erro de log
  }
}

export function getImageTraces(): { ts: number; event: string; payload: ImageTracePayload }[] {
  return [...traceBuffer];
}

export function clearImageTraces(): void {
  traceBuffer.length = 0;
}

export function exportImageTraces(): void {
  try {
    if (typeof window === 'undefined') return;
    const data = JSON.stringify(getImageTraces(), null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `debug-image-trace-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {}
}

// Formata timestamp numérico para dd-mm-yyyy - HH:MM:SS:ms
function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  const day = pad(d.getDate());
  const mon = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  const hrs = pad(d.getHours());
  const min = pad(d.getMinutes());
  const sec = pad(d.getSeconds());
  const ms = pad(d.getMilliseconds(), 3);
  return `${day}-${mon}-${year} - ${hrs}:${min}:${sec}:${ms}`;
}

// Abrevia data URLs (não registrar payloads gigantes)
function abbreviate(value: any): string {
  try {
    const str = String(value ?? '');
    if (str.startsWith('data:image')) {
      return str.slice(0, 80) + '...';
    }
    return str;
  } catch {
    return '';
  }
}

// Escapa campo CSV com aspas duplas
function csvEscape(value: string): string {
  const v = value.replace(/"/g, '""');
  return `"${v}"`;
}

export function exportImageTracesCSV(): void {
  try {
    if (typeof window === 'undefined') return;
    const rows = getImageTraces();
    // Descobrir todas as colunas de payload dinamicamente
    const payloadKeys = Array.from(
      rows.reduce((set, r) => {
        Object.keys(r.payload || {}).forEach(k => set.add(k));
        return set;
      }, new Set<string>())
    ).sort();
    // Cabeçalho: timestamp_formatado,event,<payloadKeys...>
    const header = ['timestamp', 'event', ...payloadKeys];
    const lines: string[] = [];
    lines.push(header.map(csvEscape).join(','));
    for (const r of rows) {
      const base = [formatTimestamp(r.ts), r.event];
      const values = payloadKeys.map(k => {
        const raw = (r.payload || {})[k];
        return abbreviate(raw);
      });
      lines.push([...base, ...values].map(v => csvEscape(String(v ?? ''))).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `debug-image-trace-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {}
}


